import { useCallback, useEffect, useRef, useState } from 'react';
import { getDealFleetAndRoster } from '@/services/account.service.js';

// The focused deal's OWN fleet and roster (DEAL-ROSTER-SCOPING, 2026-07-29).
//
// Deliberately a SEPARATE fetch from the account workspace's fleetAndRoster,
// which unions every deal on the account. That union is correct for the Account
// page and wrong for a deal: it rendered a pilot bound on a sibling term as a
// current pilot on this one. Same response shape, so every panel is drop-in.
//
// While in flight this returns null rather than the account aggregate. An empty
// roster for a moment is honest; the account aggregate would be the exact wrong
// pilots, which is the bug.
export default function useDealFleetAndRoster(dealId) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!!dealId);
  const aborted = useRef(false);

  const refresh = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    setError(null);
    try {
      const d = await getDealFleetAndRoster(dealId);
      if (!aborted.current) setData(d);
    } catch (err) {
      if (!aborted.current) setError(err);
    } finally {
      if (!aborted.current) setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    aborted.current = false;
    setData(null); // never show the previous deal's roster while the next loads
    if (dealId) refresh();
    return () => {
      aborted.current = true;
    };
  }, [dealId, refresh]);

  return { data, error, loading, refresh };
}
